import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, Shield } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel — Branding */}
      <div className="relative lg:w-[55%] bg-gradient-to-br from-ysrcp-800 via-ysrcp-700 to-ysrcp-600 flex flex-col items-center justify-center p-8 lg:p-12 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-white/20" />
          <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] rounded-full bg-white/10" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-lg animate-[fadeIn_0.8s_ease-out]">
          {/* Jagan Image */}
          <div className="relative mb-6">
            <div className="w-48 h-48 lg:w-64 lg:h-64 rounded-full overflow-hidden ring-4 ring-white/20 shadow-2xl">
              <img
                src="/jagan-pic.jpeg"
                alt="Sri Y.S. Jagan Mohan Reddy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/15 backdrop-blur-md rounded-full px-4 py-1.5">
              <span className="text-white text-xs font-semibold tracking-wide whitespace-nowrap">Party President</span>
            </div>
          </div>

          <h2 className="text-xl lg:text-2xl font-bold text-white mb-2">
            Sri Y.S. Jagan Mohan Reddy
          </h2>

          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-white/40 to-transparent mb-4" />

          <p className="text-white/70 text-sm lg:text-base leading-relaxed max-w-sm">
            Empowering every voice, building a better Andhra Pradesh
          </p>

          <div className="mt-6 flex items-center gap-2 text-white/40 text-xs">
            <div className="w-8 h-px bg-white/20" />
            <span>Yuvajana Sramika Rythu Congress Party</span>
            <div className="w-8 h-px bg-white/20" />
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-[#F3F5F9]">
        <div className="w-full max-w-md animate-[fadeIn_0.6s_ease-out_0.2s_both]">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-ysrcp-500 flex items-center justify-center shadow-sm">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">YSRCP Cadre Portal</h1>
                <p className="text-xs text-gray-400">Administration Panel</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">Sign in to manage cadre registrations</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-panel border border-gray-100 p-6 sm:p-8">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
                <div className="w-1 h-8 bg-red-500 rounded-full flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@ysrcpcadre.com"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ysrcp-500 focus:border-ysrcp-500 placeholder-gray-400 transition-shadow bg-gray-50/50 focus:bg-white"
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ysrcp-500 focus:border-ysrcp-500 pr-10 placeholder-gray-400 transition-shadow bg-gray-50/50 focus:bg-white"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2.5 mb-6 cursor-pointer">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="rounded border-gray-300 text-ysrcp-500 focus:ring-ysrcp-500" />
              <span className="text-sm text-gray-600">Remember me</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-ysrcp-500 text-white rounded-xl font-semibold text-sm hover:bg-ysrcp-600 transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-6">
            YSRCP Cadre Management System &middot; Confidential
          </p>
        </div>
      </div>
    </div>
  );
}
